// Open dispute — Design Pack §7.2.
//
// Pre-filled with `?exchangeId=...` from the launching exchange detail page
// (Wave 2d's exchange detail links here). On submit, the API drives both
// the dispute create + the Exchange transition to `disputed`, then redirects
// to /disputes/:id.

import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications as mantineNotifications } from '@mantine/notifications';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { AppShell } from '../../components/app-shell/AppShell';
import { MotionSection, PageHero } from '../../components/ui';
import { csrfHeaders } from '../../lib/auth-context';

const searchSchema = z.object({
  exchangeId: z.string().optional(),
});

export const Route = createFileRoute('/disputes/open')({
  validateSearch: searchSchema,
  component: OpenDispute,
});

const CATEGORY_OPTIONS: ReadonlyArray<{
  value: 'safety' | 'misrepresentation' | 'cancellation' | 'harassment' | 'cash_dispute' | 'other';
  label: string;
}> = [
  { value: 'safety', label: 'Safety' },
  { value: 'misrepresentation', label: 'Misrepresentation' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'cash_dispute', label: 'Cash dispute' },
  { value: 'other', label: 'Other' },
];

function OpenDispute() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const exchangeId = search.exchangeId;

  const [category, setCategory] = useState<(typeof CATEGORY_OPTIONS)[number]['value']>('safety');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (exchangeId === undefined || exchangeId.length === 0) {
    return (
      <AppShell tone="form">
        <Stack gap="md">
          <Title order={2}>Open a dispute</Title>
          <Alert color="yellow" title="Pick an exchange first">
            Open a dispute from the exchange it relates to. There's a link on the
            exchange page.
          </Alert>
          <Anchor component={Link} to="/exchanges">
            Back to your exchanges
          </Anchor>
        </Stack>
      </AppShell>
    );
  }

  async function handleSubmit() {
    if (description.trim().length < 20) {
      setErrorMsg('Tell us what happened. At least a sentence so we can act on it.');
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await fetch('/api/disputes', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          exchange_id: exchangeId,
          category,
          description,
        }),
      });
      if (!res.ok) {
        const j = await safeJson(res);
        setErrorMsg(j?.error?.message ?? 'Could not open dispute.');
        return;
      }
      const data = (await res.json()) as { dispute: { id: string } };
      mantineNotifications.show({
        title: 'Dispute opened.',
        message: "We'll acknowledge within 48 hours.",
      });
      void navigate({ to: '/disputes/$id', params: { id: data.dispute.id } });
    } catch {
      setErrorMsg('Could not open dispute.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitting) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Trust & safety"
            title="Open a dispute"
            lede="Open a dispute when something went wrong that you can't sort out directly. We'll acknowledge within 48 hours."
          />
        </MotionSection>

        <Box>
          <Title order={4} mb="xs">
            What kind of concern?
          </Title>
          <Radio.Group
            value={category}
            onChange={(v) =>
              setCategory(v as (typeof CATEGORY_OPTIONS)[number]['value'])
            }
          >
            <Stack gap={6}>
              {CATEGORY_OPTIONS.map((opt) => (
                <Radio key={opt.value} value={opt.value} label={opt.label} />
              ))}
            </Stack>
          </Radio.Group>
        </Box>

        <Box>
          <Title order={4}>What happened?</Title>
          <Textarea
            mt="xs"
            minRows={6}
            maxLength={4000}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder="Be specific. Times, places, what was said or done."
          />
          <Text size="xs" c="dimmed" mt={4}>
            At least a sentence (20 characters).
          </Text>
        </Box>

        <Alert color="gray">
          By submitting you confirm this is a real concern. Our team reads every
          dispute. Frivolous disputes can lead to account suspension.
        </Alert>

        {errorMsg !== null ? (
          <Alert color="red" title="We hit a snag">
            {errorMsg}
          </Alert>
        ) : null}

        <Group justify="flex-end" gap="sm">
          <Anchor href={`/exchanges/${exchangeId}`}>Cancel</Anchor>
          <Button onClick={handleSubmit} loading={submitting}>
            Open dispute
          </Button>
        </Group>
      </Stack>
    </AppShell>
  );
}

async function safeJson(res: Response): Promise<{ error?: { message?: string } } | null> {
  try {
    return (await res.json()) as { error?: { message?: string } };
  } catch {
    return null;
  }
}
